/* eslint-disable no-underscore-dangle */
/**
 * Send the PASSENGER of one trip a message, as the company, from the
 * dispatcher's trip screen — on WhatsApp or by email.
 *
 * THIS IS THE CUSTOMER CARD'S LEAD ACTION — the `primary` slot of
 * Components/TripCards' CustomerCard, filled by the dispatcher's trip screen
 * and by nobody else. Writing as the office is what the office does with a
 * passenger, so it is the green button where the card used to put a `wa.me`
 * deep link, and the deep link moved one tap away into More options
 * (PassengerMoreOptions). ONE DOOR, not two: it was its own block under the
 * card until the prominent button was still the wrong one, which is a thing
 * a second entry point cannot fix.
 *
 * NOT THE SAME KIND OF THING as the deep link it displaced. That one opens
 * the dispatcher's OWN WhatsApp, the passenger sees a message from a personal
 * number, and the reply comes back to that phone. These go out from the
 * company's number and the company's address, and the replies land where the
 * next person on shift can read them. A sender who confuses the two waits for
 * an answer that was never coming to them, so the card says which number this
 * button leaves from, the sheet behind More options says where the other
 * one's replies go, and this sheet says it again at the moment of sending.
 * The deep link itself is untouched — at the kerb, opening your own chat is
 * still the right tool.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE CHANNEL IS A FIELD IN THIS SHEET, NOT A SECOND BUTTON ON THE CARD.
 *
 * Email could have been a second lead action beside "Message as the office".
 * It is a toggle inside the composer instead, for four reasons:
 *
 *   ONE DOOR, again. The card's action row already wraps at phone width with
 *   one `--lead` button and the Call beside it (see `pp-actions--lead` in
 *   Components/TripCards). A third equal-weight control turns that row into
 *   a grid of chips — which is exactly what CrewSheet exists to avoid, and
 *   what this very card got wrong the first time.
 *
 *   THE SENDER'S INTENT IS "TELL THE PASSENGER SOMETHING". The channel is how
 *   it travels. Deciding it inside the sheet also means it can be decided
 *   AFTER reading the trip and starting to type, which is the real order
 *   people work in — not before, from a button label.
 *
 *   THE CARD'S SLOT TAKES ONE NODE, and the slots are what keep office
 *   messaging off the driver's and the operator's screens (`POST
 *   /portal/message` 403s both). A second lead action means a third opt-in
 *   slot on a card shared by three screens — more surface for exactly the
 *   regression those slots were invented to prevent.
 *
 *   AND THE DASHBOARD'S COMPOSER IS ALREADY ONE COMPONENT WITH A CHANNEL
 *   (Orders/Components/PassengerMessageModal). Two composers here would be
 *   the pair that drifts.
 *
 * THE ARGUMENT AGAINST IT IS REAL, and it is what the shape change answers: a
 * two-state control can be missed, and somebody who misses it sends a
 * WhatsApp when they meant an email. So the channel is the FIRST field in the
 * sheet, above everything; the sheet's title, its note, its fields and its
 * send button all change with it; and the composer changes SHAPE, not just
 * wording — a preview and a one-line box on one side, a subject and a
 * paragraph box on the other. You cannot be in email mode and believe you
 * are in WhatsApp mode, because the form does not look the same. A toggle
 * that only swapped a label would not be safe and should not have been built.
 *
 * EACH CHANNEL KEEPS ITS OWN DRAFT. Switching does not carry the words
 * across, and switching back brings them back untouched. Carrying them would
 * mean flattening three email paragraphs into one line the moment somebody
 * tapped "WhatsApp" to look at it — losing work on a curious tap. The two
 * bodies are different texts anyway: different saved messages, different
 * length, different shape. (Same split as the dashboard's composer, which
 * has held `text` and `html` separately from the start.)
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHAT THE SENDER WRITES ON WHATSAPP IS ONE OF THREE BLOCKS. The passenger
 * receives the approved template — a greeting with their own first name, a
 * fixed line saying there is a message, then the body. The sheet shows all
 * three as they will be read, because a sender who cannot see the greeting
 * writes "Hi Anna" into the body and Anna is greeted twice. The name is shown
 * and NOT sent: the server derives both it and the number from the booking.
 *
 * AND THE WHATSAPP BODY IS ONE LINE, without ever saying so as an error. The
 * server flattens a template variable anyway (`toTemplateVariable`), so a
 * newline does not fail the send — it arrives as one run-on line the
 * passenger has to read. That makes this a question of what they receive, not
 * of what the transport allows, and the field is shaped so the good version
 * is the only one that can be typed: a single-line input, so Enter submits
 * instead of breaking the line, and a pasted paragraph flattened to SPACES as
 * it arrives rather than rejected. Nobody is punished for a paste.
 *
 * The paste is intercepted deliberately. Left to the browser, a single-line
 * input strips newlines instead of replacing them — "...is here.He is at
 * exit B." — which is worse than either the paragraph or a clean line, and
 * it happens before any change handler can see it. `pasteOneLine` holds the
 * detail.
 *
 * THE LENGTH CAP IS REAL, AND IT IS WHATSAPP'S. `POST /portal/message`
 * refuses a WhatsApp body over `TEMPLATE_VARIABLE_MAX` with a 400, because
 * Meta rejects an over-long template variable at its own edge and the
 * passenger would then receive nothing at all. So the count is shown against
 * the limit from the first character, and going over disables the send with
 * the number to cut — it does NOT truncate. Silently dropping the end of a
 * message loses the half that mattered; the cap exists precisely so the
 * office can shorten it deliberately.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * AN EMAIL IS NONE OF THAT, AND IS NOT A RELABELLED WHATSAPP.
 *
 *   IT HAS A SUBJECT, prefilled with the booking reference and editable. A
 *   passenger who booked months ago is reading a crowded inbox and the
 *   reference is the one string they can match against their confirmation.
 *
 *   IT HAS NO ONE-LINE RULE AND NO CAP. There is no template variable, so
 *   paragraphs survive and are SENT WITH THEIR NEWLINES INTACT. The field is
 *   a textarea: Enter makes a new line, as it must, and the send is the
 *   button. Forcing an email through the WhatsApp field would be a worse
 *   message for no reason at all.
 *
 *   IT HAS NO TEMPLATE TO PREVIEW. `we_have_a_message_for_you` does not
 *   apply, so the three-block preview is not shown — a preview of a greeting
 *   that will not be sent is a lie, not a reassurance.
 *
 *   AND THE REPLY GOES SOMEWHERE ELSE. A WhatsApp reply lands in the
 *   respond.io shared inbox; an office email leaves from the company address
 *   and the answer arrives in the OFFICE inbox — not in the dispatcher's own
 *   mail. Said in the sheet on each channel, because looking for an answer
 *   in the wrong place is the failure this copy exists to prevent.
 *
 *   THERE IS NO RICH EDITOR, on purpose. `HtmlEditor` ships ESM the test
 *   runner cannot transform (it is behind a `lazy()` boundary in the
 *   dashboard for exactly that reason), and a formatting toolbar is the wrong
 *   control on a phone at the kerb. Plain multi-line text.
 *
 * THE ADDRESS IS NEVER HERE. The portal is not given the passenger's email
 * address and does not send one: the server derives the recipient from the
 * booking, and refuses a client-supplied address outright
 * (Services/PortalMessageApi). So there is nothing to put a trigger beside
 * and nothing to mistype — which is why the dashboard gets a control next to
 * the address it can see, and this screen gets a channel inside the sheet.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * NOT IN THE CREW SHEET, either. The trip screen's controls were collapsed
 * into sheets precisely because a row of equal-weight chips is unusable on a
 * phone (CrewSheet holds that reasoning), and the same reasoning keeps the
 * passenger's controls out of the CREW's sheet: that one is what you can do
 * to the crew, and the passenger is not crew. The composing happens in the
 * portal's own bottom sheet — the idiom, not the drawer.
 *
 * It renders nothing at all for a trip with no passenger first name to greet
 * — there would be no `{{1}}` to show and nothing honest to preview. The card
 * then keeps the plain `wa.me` link as its lead, which is what it had before
 * any of this: an empty slot is the old card, not a gap.
 */
import React, { useState } from 'react';
import BottomSheet from '../Components/BottomSheet';
import Toast from '../Components/Toast';
import { MailIcon, WhatsAppIcon } from '../Components/Icons';
import { firstName } from '../portalFormat';
import { operatorErrorMessage } from '../Operator/operatorFormat';
import { sendPassengerMessage } from '../../../Services/PortalMessageApi';
import {
  flattenLines, hasLineBreak, oneLine, pasteOneLine,
} from '../../../Utils/oneLine';
import {
  looksUnresolved, templateBlocks, TEMPLATE_VARIABLE_MAX,
} from '../../../Utils/passengerTemplate';
import {
  defaultEmailSubject, EMAIL, isEmailChannel, presetsForChannel, WHATSAPP,
} from '../../../Utils/messageChannel';

const PLACEHOLDER = 'Your message appears here';

/** The channel toggle's two items, in the order they are offered. */
const CHANNELS = [
  { value: WHATSAPP, label: 'WhatsApp' },
  { value: EMAIL, label: 'Email' },
];

/**
 * Where a reply to this channel will actually arrive — the one thing a sender
 * can get wrong without noticing until they are waiting for an answer.
 */
const CHANNEL_NOTE = {
  [WHATSAPP]: 'Sent from the company WhatsApp number, not yours — the reply lands in the '
    + 'shared inbox for whoever is on shift, not on your phone.',
  [EMAIL]: 'Sent from the company email address, not yours — the reply lands in the office '
    + 'inbox, not in your own mail. The address comes off the booking; you never type it.',
};

/** The template as the passenger will read it: the two fixed blocks, then the body. */
function TemplatePreview({ first, body }) {
  const blocks = templateBlocks({ first, body, placeholder: PLACEHOLDER });

  return (
    <div className="pp-pmsg__preview">
      {blocks.map((block) => (
        <p
          key={block.id}
          className={[
            'pp-pmsg__block',
            block.fixed ? 'pp-pmsg__block--fixed' : '',
            block.empty ? 'pp-pmsg__block--empty' : '',
          ].filter(Boolean).join(' ')}
          data-testid={`passenger-message-${block.id}`}
        >
          {block.text}
        </p>
      ))}
    </div>
  );
}

function PassengerMessageSheet({
  trip, first, presets, onClose, onSent,
}) {
  const [channel, setChannel] = useState(WHATSAPP);
  // One draft per channel — see EACH CHANNEL KEEPS ITS OWN DRAFT above.
  const [text, setText] = useState('');
  const [emailText, setEmailText] = useState('');
  const [subject, setSubject] = useState(defaultEmailSubject(trip?.booking_reference));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const email = isEmailChannel(channel);
  const sendLabel = email ? 'Send email' : 'Send on WhatsApp';

  // What would go on the wire for WhatsApp — the emptiness test, so a body of
  // nothing but spaces does not arm the button.
  const wire = oneLine(text);
  const over = wire.length - TEMPLATE_VARIABLE_MAX;
  // The body of whichever channel is in use, for the advice below the field.
  const written = email ? emailText : wire;
  const ready = email
    ? Boolean(oneLine(subject)) && Boolean(emailText.trim()) && !busy
    : Boolean(wire) && over <= 0 && !busy;
  // A variable nobody filled in. Advice, not a block — the server refuses
  // the send itself (see Utils/passengerTemplate).
  const unresolved = looksUnresolved(written);

  // Only this channel's saved messages. An old row with no `channel` is a
  // WhatsApp one, which is what keeps the two already in production working.
  const chips = presetsForChannel(presets, channel);

  /**
   * Fill the composer from a saved message, leaving everything editable.
   *
   * An email snippet carries BOTH halves, so it fills both — but a snippet
   * with no subject of its own leaves the prefilled one alone rather than
   * blanking a subject the sender could already see.
   */
  const applyPreset = (preset) => {
    if (email) {
      if (preset.subject) setSubject(preset.subject);
      setEmailText(preset.body || '');
      return;
    }
    setText(flattenLines(preset.body));
  };

  const submit = async () => {
    if (!ready) return;

    setBusy(true);
    setError('');
    try {
      // WhatsApp sends no `channel` at all: the API defaults it, and the live
      // path stays byte-identical (Services/PortalMessageApi).
      const sent = await sendPassengerMessage(email
        ? {
          bookingId: trip._id, channel: EMAIL, subject, text: emailText,
        }
        : { bookingId: trip._id, text });
      // The server is authoritative on who it greeted — it derives `{{1}}`
      // itself — so its `first_name` wins over the one on screen. It
      // deliberately echoes back no phone number, no address and no message
      // text. Unmounts this sheet, so nothing is set on it afterwards.
      onSent(sent?.first_name || first, channel);
    } catch (failure) {
      // The typed text is LEFT ALONE. A send that failed is the one moment
      // somebody must not have to write their message again.
      setError(operatorErrorMessage(failure, {
        400: email
          ? 'That email could not be sent as written. Check the subject and the message, '
            + 'then try again.'
          : `That message is too long to send. Shorten it to ${TEMPLATE_VARIABLE_MAX} `
            + 'characters or fewer and try again.',
        404: 'This booking is no longer on the board, so no message can be sent against it.',
        409: 'The passenger on this booking changed in the meantime. Reopen the trip and try again.',
      }));
      setBusy(false);
    }
  };

  return (
    <BottomSheet
      title={email ? 'Email the passenger' : 'Message the passenger'}
      subtitle={[trip?.booking_reference, trip?.customer_name].filter(Boolean).join(' · ')}
      onClose={onClose}
      closeDisabled={busy}
      closeLabel="Close"
    >
      <form
        className="pp-form pp-pmsg"
        onSubmit={(event) => { event.preventDefault(); submit(); }}
      >
        {/* THE FIRST THING IN THE SHEET. A mode this far down could be
            missed; everything below it changes to match. */}
        <div className="pp-field">
          <span className="pp-field__label" id="pp-pmsg-channel">How it goes out</span>
          <div className="pp-seg" role="group" aria-labelledby="pp-pmsg-channel">
            {CHANNELS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`pp-seg__item${channel === option.value ? ' pp-seg__item--on' : ''}`}
                aria-pressed={channel === option.value}
                disabled={busy}
                onClick={() => setChannel(option.value)}
              >
                {option.value === EMAIL ? <MailIcon size={17} /> : <WhatsAppIcon size={17} />}
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <p className="pp-sheet__note">{CHANNEL_NOTE[channel]}</p>

        {/* WHATSAPP ONLY: the approved template has a greeting the sender
            must be able to see. An email has no template, and previewing one
            that will not be sent would be a lie. */}
        {email ? null : (
          <div className="pp-field">
            {/* The name is DISPLAY AND CONFIRM. It is not a field and it is
                not sent: the server fills the greeting from the booking. */}
            <span className="pp-field__label">{`What ${first} receives`}</span>
            <TemplatePreview first={first} body={text} />
          </div>
        )}

        {/* EMAIL ONLY: prefilled from the booking and editable. The subject
            is what a passenger recognises the mail by in a full inbox. */}
        {email ? (
          <div className="pp-field">
            <label className="pp-field__label" htmlFor="pp-pmsg-subject">Subject</label>
            <input
              id="pp-pmsg-subject"
              className="pp-input"
              type="text"
              value={subject}
              autoComplete="off"
              placeholder="Your booking"
              disabled={busy}
              onChange={(event) => setSubject(flattenLines(event.target.value))}
            />
          </div>
        ) : null}

        {/* THE PRESET SEAM, one channel at a time. Saved messages arrive
            already ordered; picking one FILLS the field(s) and leaves them
            editable, so a preset is a starting point rather than a thing you
            send blind. Nothing but the body — and, on email, the subject —
            ever reaches the wire: a label is for the sender. */}
        {chips.length ? (
          <div className="pp-pmsg__presets" role="group" aria-label="Saved messages">
            {chips.map((preset) => (
              <button
                key={preset.id || preset.label}
                type="button"
                className="pp-btn pp-btn--sm pp-btn--outline"
                onClick={() => applyPreset(preset)}
                disabled={busy}
              >
                {preset.label}
              </button>
            ))}
          </div>
        ) : null}

        <div className="pp-field">
          <label className="pp-field__label" htmlFor={email ? 'pp-pmsg-email-body' : 'pp-pmsg-body'}>
            Your message
          </label>

          {email ? (
            <>
              {/* Paragraphs, kept. Enter makes a new line here — the send is
                  the button, because an email with no line breaks in it is
                  the thing this channel exists to avoid. */}
              <textarea
                id="pp-pmsg-email-body"
                className="pp-input pp-input--area pp-pmsg__area"
                rows={7}
                value={emailText}
                placeholder={'Your driver will meet you at exit B.\n\n'
                  + 'Look for a sign with your name on it.'}
                disabled={busy}
                onChange={(event) => setEmailText(event.target.value)}
              />
              <p className="pp-sheet__note">
                Write as many paragraphs as it needs — an email has no one-line rule and no
                length limit. Press the button to send.
              </p>
            </>
          ) : (
            <>
              <input
                id="pp-pmsg-body"
                className="pp-input"
                type="text"
                value={text}
                autoComplete="off"
                placeholder="Your driver is waiting at exit B"
                disabled={busy}
                // A single-line input cannot HOLD a line break, and a paste
                // that arrives with some is flattened rather than refused.
                onChange={(event) => setText(flattenLines(event.target.value))}
                // A multi-line paste is intercepted rather than left to the
                // browser, which would STRIP the newlines and weld the words
                // together before any onChange could see them (pasteOneLine).
                // A paste with nothing to flatten keeps native behaviour, so
                // the caret still lands where it should.
                onPaste={(event) => {
                  const pasted = event.clipboardData?.getData('text') || '';
                  if (!hasLineBreak(pasted)) return;
                  event.preventDefault();
                  setText(pasteOneLine({
                    value: text,
                    pasted,
                    start: event.target.selectionStart,
                    end: event.target.selectionEnd,
                  }));
                }}
                // Explicit rather than relying on a form's implicit
                // submission, so Enter does the same thing in every browser.
                // WhatsApp ONLY: in the email textarea Enter is a new line.
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    submit();
                  }
                }}
              />
              <p className="pp-sheet__note">
                One line. Press Enter to send — a line break would reach the passenger as
                one run-on line, so a pasted paragraph becomes spaces.
                <span
                  className={`pp-pmsg__count${over > 0 ? ' pp-pmsg__count--over' : ''}`}
                  data-testid="passenger-message-count"
                >
                  {` ${wire.length}/${TEMPLATE_VARIABLE_MAX}`}
                </span>
              </p>

              {/* Always visible while it applies, never only after a press:
                  the send button is disabled, and a sender who cannot see
                  why has no way to find out. */}
              {over > 0 ? (
                <p className="pp-form__error" role="alert" data-testid="passenger-message-too-long">
                  {`Too long to send by ${over} character${over === 1 ? '' : 's'}. `
                    + 'Shorten it and the send will arm itself.'}
                </p>
              ) : null}
            </>
          )}

          {unresolved ? (
            <p className="pp-form__error" data-testid="passenger-message-unresolved">
              {`${unresolved} will reach the passenger exactly as written. `
                + 'Replace it with the real words.'}
            </p>
          ) : null}
        </div>

        {error ? <div className="pp-form__error" role="alert">{error}</div> : null}

        <div className="pp-actions">
          <button
            type="button"
            className="pp-btn pp-btn--outline pp-btn--grow pp-btn--tall"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="pp-btn pp-btn--dark pp-btn--grow pp-btn--tall"
            disabled={!ready}
          >
            {busy ? 'Sending…' : sendLabel}
          </button>
        </div>
      </form>
    </BottomSheet>
  );
}

export default function PassengerMessage({ trip, presets = [] }) {
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState('');

  // `{{1}}` is a first name. With nothing to put in it there is nothing
  // honest to preview, so the control is not offered at all.
  const first = firstName(trip?.customer_name);
  if (!first) return null;

  return (
    <>
      {/* The green, widest control on the card — the weight the `wa.me` link
          used to have. `--grow` and `--contact` are the card's own lead
          classes, so this sits in the row at the height everything else in
          it is.

          STILL THE WHATSAPP GLYPH, and the label stays channel-neutral:
          WhatsApp is the channel the sheet opens on and by far the common
          one, so the icon names the default rather than overpromising. The
          button offers to write to the passenger as the office; the sheet is
          where the channel is chosen. */}
      <button
        type="button"
        className="pp-btn pp-btn--whatsapp pp-btn--grow pp-btn--contact"
        onClick={() => setOpen(true)}
        aria-label={`Message the passenger as the office on ${trip?.booking_reference}`}
      >
        <WhatsAppIcon size={19} />
        Message as the office
      </button>

      {open ? (
        <PassengerMessageSheet
          trip={trip}
          first={first}
          presets={presets}
          onClose={() => setOpen(false)}
          onSent={(name, channel) => {
            setOpen(false);
            setNotice(isEmailChannel(channel)
              ? `Email sent to ${name}`
              : `Message sent to ${name} on WhatsApp`);
          }}
        />
      ) : null}

      <Toast message={notice} onDone={() => setNotice('')} />
    </>
  );
}
